// LAP-550 V1.2 — Page détail "Répartition de mes investissements"
import React, { useEffect, useState, useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined'
import { useTranslation } from 'react-i18next'
import { makeGetRequest } from 'utils/api'
import DetailPageLayout from '../../_shared/DetailPageLayout'
import AssetTable from '../../_shared/AssetTable'
import { invTheme } from '../../_shared/theme'
import { textCol, moneyCol, percentCol, toNum } from '../../_shared/gridCols'

const PALETTE = ['#63a0ff', '#a78bfa', '#22d3ee', '#34d399', '#f59e0b', '#f472b6', '#f97316', '#60a5fa', '#c084fc', '#fbbf24']

export default function RepartitionDetail() {
  const { t } = useTranslation('layout')
  const [report, setReport] = useState(null)
  const [view, setView] = useState('type')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const fetch = async () => {
      const { data } = await makeGetRequest('/api/investment/reports/', {}, true)
      const r = (data || []).find((x) => x.type === 'breakdown-investment')
      setReport(r?.data || null)
    }
    fetch()
  }, [])

  const chartData = useMemo(() => {
    if (!report) return null
    return {
      labels: report.labels || [],
      datasets: [
        {
          data: report.data || [],
          backgroundColor: PALETTE,
          borderColor: 'rgba(0,0,0,0)',
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    }
  }, [report])

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      datalabels: {
        color: 'white',
        font: { size: 11, weight: 600 },
        formatter: (v, ctx) => `${ctx.chart.data.labels[ctx.dataIndex]}\n${v}%`,
        textAlign: 'center',
        display: 'auto',
      },
      legend: { display: false },
    },
  }

  const columns = useMemo(
    () => [
      textCol({
        field: 'category',
        headerName: t('investments.detail.cols.category', 'Catégorie'),
        width: 200,
        valueGetter: (p) => p.row.label ?? p.row.category ?? p.row.type ?? '',
      }),
      moneyCol({ field: 'value', headerName: t('investments.detail.cols.amount', 'Montant'), width: 150 }),
      percentCol({
        field: 'share',
        headerName: t('investments.detail.cols.share', 'Part'),
        width: 120,
        getter: (p) => toNum(p.row.share ?? p.row.percentage),
      }),
    ],
    [t],
  )

  // Les cases manquantes restent `null` : y écrire « — » ferait trier ces
  // lignes comme du texte et les remonterait en tête du tri décroissant.
  const tableRows = report?.labels?.map((lab, i) => ({
    id: lab + i,
    label: lab,
    value: report.values?.[i] ?? null,
    share: report.data?.[i] ?? null,
  })) || []

  return (
    <DetailPageLayout
      title={t('investments.portfolio.repartition.title')}
      subtitle={t('investments.detail.repartitionSubtitle', 'Répartition de mes investissements')}
      icon={<PieChartOutlineOutlinedIcon fontSize="small" />}
      accent={invTheme.accents.repartition}
      chartSubtitleOptions={[
        { value: 'type', label: t('investments.detail.byType', 'Par type') },
        { value: 'sector', label: t('investments.detail.bySector', 'Par secteur') },
      ]}
      chartSubtitle={view}
      onChartSubtitleChange={setView}
      sumLabel={t('investments.detail.totalSum', 'Total')}
      sumValue={report?.total !== undefined ? `${report.total} €` : '—'}
      chart={chartData ? <Doughnut data={chartData} options={chartOptions} /> : null}
      tableTitle={t('investments.detail.repartitionTableTitle', 'Répartition de mes investissements')}
      tableCount={tableRows.length}
      tableContent={
        <AssetTable
          rows={tableRows}
          columns={columns}
          emptyLabel={t('investments.portfolio.noInvestmentsYet')}
          searchable
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('investments.detail.searchCategory', 'Rechercher une catégorie…')}
        />
      }
    />
  )
}
